import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import paymentService from '../../services/paymentService';
import { useGlobalState } from '../../state';
import { toCurrency } from '../../utils/formaters';
import CustomPicker from '../../components/CustomPicker';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FaCamera, FaFilePdf, FaImage, FaCheckCircle } from 'react-icons/fa';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';

const CUOTA_CONVENIO_ID = '290';
const tipoCuotaOptions = [
    { label: 'Anticipo', value: '1' },
    { label: 'Cuota', value: '2' },
];

const validationSchema = yup.object().shape({
    monto: yup.number().required('El monto es obligatorio').positive('El monto debe ser un número positivo').typeError('El monto debe ser un número'),
    concepto: yup.string().required('El Concepto es requerido'),
    mes: yup.string().when('concepto', {
        is: (concepto: string) => concepto !== CUOTA_CONVENIO_ID,
        then: (schema) => schema.required('El mes es requerido'),
        otherwise: (schema) => schema.notRequired(),
    }),
    anio: yup.string().when('concepto', {
        is: (concepto: string) => concepto !== CUOTA_CONVENIO_ID,
        then: (schema) => schema.required('El año es requerido'),
        otherwise: (schema) => schema.notRequired(),
    }),
    nroPlan: yup.string().when('concepto', {
        is: (concepto: string) => concepto === CUOTA_CONVENIO_ID,
        then: (schema) => schema.required('El Nro. de Plan es requerido').matches(/^\d+$/, 'El Nro. de Plan debe contener solo dígitos'),
        otherwise: (schema) => schema.notRequired(),
    }),
    tipoCuota: yup.string().when('concepto', {
        is: (concepto: string) => concepto === CUOTA_CONVENIO_ID,
        then: (schema) => schema.required('El Tipo de Cuota es requerido').oneOf(['1', '2'], 'Tipo de Cuota inválido'),
        otherwise: (schema) => schema.notRequired(),
    }),
    nroCuota: yup.string().when('concepto', {
        is: (concepto: string) => concepto === CUOTA_CONVENIO_ID,
        then: (schema) => schema.required('El Nro. de Cuota es requerido').matches(/^\d+$/, 'El Nro. de Cuota debe contener solo dígitos'),
        otherwise: (schema) => schema.notRequired(),
    }),
});

const months = [
    { label: '01', value: '01' }, { label: '02', value: '02' }, { label: '03', value: '03' },
    { label: '04', value: '04' }, { label: '05', value: '05' }, { label: '06', value: '06' },
    { label: '07', value: '07' }, { label: '08', value: '08' }, { label: '09', value: '09' },
    { label: '10', value: '10' }, { label: '11', value: '11' }, { label: '12', value: '12' },
];

const STORAGE_KEY = 'transferenciaTemporal';

const Transferencia: React.FC = () => {
    const navigate = useNavigate();
    const [user] = useGlobalState('user');

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [anios, setAnios] = useState<any[]>([]);
    const [conceptos, setConceptos] = useState<any[]>([]);
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string | undefined>(undefined);
    const [fileUrl, setFileUrl] = useState<string | undefined>(undefined);
    const [fileType, setFileType] = useState<'image' | 'pdf'>('image');
    const [codigoTransaccion, setCodigoTransaccion] = useState<string | undefined>();
    const [transferencia, setTransferencia] = useState<any>(undefined);
    const [showCamera, setShowCamera] = useState<boolean>(false);
    const [cameraKey, setCameraKey] = useState<number>(0);
    const [displayMonto, setDisplayMonto] = useState<string>('0,00');
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: { monto: 0, concepto: '', mes: '', anio: '', nroPlan: '', tipoCuota: '', nroCuota: '' }
    });

    const selectedConcepto = watch('concepto');
    const isCuotaConvenio = selectedConcepto === CUOTA_CONVENIO_ID;

    useEffect(() => {
        const monto = watch('monto');
        const numeric = typeof monto === 'number' && !isNaN(monto) ? monto : 0;
        const formatted = new Intl.NumberFormat('es-ES', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numeric);
        setDisplayMonto(formatted);
    }, [watch]);

    const formatNumberEs = (n: number) =>
        new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

    const handleMontoChange = (raw: string) => {
        const digits = raw.replace(/\D/g, '');
        const centsInt = digits === '' ? 0 : parseInt(digits, 10);
        const numeric = centsInt / 100;

        setValue('monto', numeric, { shouldValidate: true, shouldDirty: true });
        setDisplayMonto('$' + formatNumberEs(numeric));
    };

    const handleMontoFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.setSelectionRange(e.target.value.length, e.target.value.length);
    };

    useEffect(() => {
        if (isCuotaConvenio) {
            setValue('mes', '', { shouldValidate: true });
            setValue('anio', '', { shouldValidate: true });
            return;
        }

        setValue('nroPlan', '', { shouldValidate: true });
        setValue('tipoCuota', '', { shouldValidate: true });
        setValue('nroCuota', '', { shouldValidate: true });
    }, [isCuotaConvenio, setValue]);

    useEffect(() => {
        if (user === undefined) return;
        if (!user) {
            navigate('/auth/signin');
            return;
        }

        // years list
        const hoy = new Date().getFullYear();
        const years: any[] = [];
        for (let i = 0; i < 15; i++) years.push({ label: (hoy - i).toString(), value: (hoy - i).toString() });
        setAnios(years);

        // fetch conceptos
        setLoading(true);
        paymentService.getConceptosBoleta?.().then((result: any) => {
            if (result && result.success) {
                setConceptos((result.data || []).map((x: any) => ({ label: x.descripcion, value: String(x.id) })));
            } else {
                setError(result?.errors?.[0] ?? 'Ocurrió un error al obtener conceptos');
            }
        }).catch((ex: any) => setError(ex?.message ?? 'Ocurrió un error')).finally(() => setLoading(false));
    }, [user, navigate]);

    // file selection (web)
    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setFileName(f.name);
        setFileUrl(URL.createObjectURL(f));
        setFileType(f.type.startsWith('image/') ? 'image' : 'pdf');
    };

    const removeFile = () => {
        setFile(null);
        setFileName(undefined);
        if (fileUrl) {
            URL.revokeObjectURL(fileUrl);
            setFileUrl(undefined);
        }
    };

    const onSubmit = async (data: any) => {
        setError(null);

        if (!file) {
            setError('Debe adjuntar un comprobante para continuar');
            return;
        }

        try {
            setLoading(true);

            const formData = new FormData();
            formData.append('File', file);
            formData.append('FileType', fileType);
            // Keep the same TipoPago string used in other migrated pages
            formData.append('TipoPago', 'TRANSFERENCIA');
            formData.append('Periodo', isCuotaConvenio ? '' : `${data.anio}${data.mes}`);
            formData.append('Concepto', String(data.concepto));
            formData.append('Monto', String(data.monto));
            const metadata: any = {
                descripcion: (conceptos.find(c => String(c.value) === String(data.concepto))?.label ?? ''),
            };

            if (isCuotaConvenio) {
                metadata.nroPlan = data.nroPlan;
                metadata.tipoCuota = data.tipoCuota;
                metadata.nroCuota = data.nroCuota;
            }

            formData.append('Metadata', JSON.stringify(metadata));

            // Save transfer object for confirmation UI
            setTransferencia({
                periodo: isCuotaConvenio ? '' : `${data.anio}${data.mes}`,
                concepto: conceptos.find(c => String(c.value) === String(data.concepto))?.label,
                monto: data.monto,
                nroPlan: data.nroPlan,
                tipoCuota: data.tipoCuota,
                nroCuota: data.nroCuota,
            });

            const response = await paymentService.crearPago(formData);
            setLoading(false);

            if (response && response.success) {
                setCodigoTransaccion(String(response.data));
                try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ formData: true, result: response.data })); } catch { }
            } else {
                setError(response?.errors?.[0] ?? 'Ocurrió un error al informar la transferencia');
            }
        } catch (ex: any) {
            setLoading(false);
            setError(ex?.message ?? 'Ocurrió un error inesperado');
        }
    };

    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
            }
        };
    }, []);

    // success view
    if (codigoTransaccion) {
        return (
            <DefaultLayout>
                <div className="p-2">
                    <div className="md:max-w-6xl mx-auto bg-white md:p-6 rounded">
                        <div className="flex justify-center mb-4">
                            <FaCheckCircle className="text-4xl text-primario-celeste-800" />
                        </div>
                        <h2 className="text-lg font-semibold mb-2">Confirmación de recepción de informe de transferencia</h2>
                        <p className="text-sm text-gray-600 mb-6">
                            El informe se encuentra en proceso de validación. Si todo está bien, verás el resultado en el saldo de tu cuenta.
                        </p>

                        <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded p-4">
                            <div className="text-sm">Fecha de Recepción</div>
                            <div className="text-sm font-semibold text-right">{new Date().toLocaleString()}</div>

                            {transferencia?.periodo && (
                                <>
                                    <div className="text-sm">Periodo</div>
                                    <div className="text-sm font-semibold text-right">{transferencia?.periodo}</div>
                                </>
                            )}

                            <div className="text-sm">Concepto</div>
                            <div className="text-sm font-semibold text-right">{transferencia?.concepto}</div>

                            <div className="text-sm">Número de referencia</div>
                            <div className="text-sm font-semibold text-right">TRF-{codigoTransaccion}</div>

                            <div className="text-sm">Importe ingresado</div>
                            <div className="text-sm font-bold text-right">{toCurrency(transferencia?.monto)}</div>
                        </div>

                        <div className="mt-6">
                            <button onClick={() => navigate('/')} className="px-6 py-3 rounded bg-primario-celeste-800 text-white font-semibold">Volver al inicio</button>
                        </div>
                    </div>
                </div>
            </DefaultLayout>
        );
    }

    const cerrarCamara = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setShowCamera(false);
    };

    const abrirCamara = async () => {
        try {
            if (!navigator.mediaDevices?.getUserMedia) {
                setError('Este navegador no soporta acceso a cámara');
                return;
            }

            setShowCamera(false);

            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }

            setCameraKey((prev) => prev + 1);

            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' },
                audio: false,
            });

            streamRef.current = stream;
            setShowCamera(true);

            setTimeout(async () => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }
            }, 150);
        } catch {
            setError('No se pudo acceder a la cámara');
            setShowCamera(false);
        }
    };

    const sacarFoto = () => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        if (video.readyState < 2 || video.videoWidth === 0) {
            setError('La cámara todavía no está lista');
            return;
        }

        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
            if (!blob) return;
            const foto = new File([blob], 'comprobante.jpg', { type: 'image/jpeg' });
            setFile(foto);
            setFileName(foto.name);
            setFileUrl(URL.createObjectURL(blob));
            setFileType('image');
        }, 'image/jpeg');

        cerrarCamara();
    };

    return (
        <DefaultLayout>
            <Breadcrumb pageName="Informar transferencia" />

            <div className="p-6">
                <div className="md:max-w-6xl mx-auto bg-white md:p-6 rounded">


                    {loading && <Loading statusText="Cargando" />}

                    {error && <Alert type="failure" message={error} />}

                    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(onSubmit)(); }}>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Monto abonado</label>
                            <Controller
                                control={control}
                                name="monto"
                                render={() => (
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={displayMonto}
                                        onChange={(e) => handleMontoChange(e.target.value)}
                                        onFocus={handleMontoFocus}
                                        className={`mt-1 block w-full rounded-md border ${errors.monto ? 'border-red-500' : 'border-gray-300'} p-2`}
                                        placeholder="0,00"
                                    />
                                )}
                            />
                            {errors.monto && <p className="text-red-600 text-sm mt-1">{(errors.monto as any).message}</p>}
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Concepto</label>
                            <Controller
                                control={control}
                                name="concepto"
                                render={({ field: { onChange, value } }) => (
                                    <CustomPicker
                                        options={conceptos}
                                        onValueChange={(v: string | number) => onChange(String(v))}
                                        selectedValue={value}
                                        placeholder="Seleccione una opción"
                                    />
                                )}
                            />
                            {errors.concepto && <p className="text-red-600 text-sm mt-1">{(errors.concepto as any).message}</p>}
                        </div>

                        {!isCuotaConvenio && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Periodo a pagar</label>
                                <div className="flex gap-3 mt-2">
                                    <div className="flex-1">
                                        <Controller
                                            control={control}
                                            name="mes"
                                            render={({ field: { onChange, value } }) => (
                                                <CustomPicker
                                                    options={months}
                                                    onValueChange={(v: string | number) => onChange(String(v))}
                                                    selectedValue={value}
                                                    placeholder="Mes..."
                                                />
                                            )}
                                        />
                                        {errors.mes && <p className="text-red-600 text-sm mt-1">{(errors.mes as any).message}</p>}
                                    </div>

                                    <div className="flex-1">
                                        <Controller
                                            control={control}
                                            name="anio"
                                            render={({ field: { onChange, value } }) => (
                                                <CustomPicker
                                                    options={anios}
                                                    onValueChange={(v: string | number) => onChange(String(v))}
                                                    selectedValue={value}
                                                    placeholder="Año..."
                                                />
                                            )}
                                        />
                                        {errors.anio && <p className="text-red-600 text-sm mt-1">{(errors.anio as any).message}</p>}
                                    </div>
                                </div>
                            </div>
                        )}

                        {isCuotaConvenio && (
                            <>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700">Nro. de Plan</label>
                                    <Controller
                                        control={control}
                                        name="nroPlan"
                                        render={({ field: { onChange, value } }) => (
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                value={value ?? ''}
                                                onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
                                                className={`mt-1 block w-full rounded-md border ${errors.nroPlan ? 'border-red-500' : 'border-gray-300'} p-2`}
                                            />
                                        )}
                                    />
                                    {errors.nroPlan && <p className="text-red-600 text-sm mt-1">{(errors.nroPlan as any).message}</p>}
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700">Tipo de Cuota</label>
                                    <Controller
                                        control={control}
                                        name="tipoCuota"
                                        render={({ field: { onChange, value } }) => (
                                            <CustomPicker
                                                options={tipoCuotaOptions}
                                                onValueChange={(v: string | number) => onChange(String(v))}
                                                selectedValue={value}
                                                placeholder="Seleccione una opción"
                                            />
                                        )}
                                    />
                                    {errors.tipoCuota && <p className="text-red-600 text-sm mt-1">{(errors.tipoCuota as any).message}</p>}
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700">Nro. de Cuota</label>
                                    <Controller
                                        control={control}
                                        name="nroCuota"
                                        render={({ field: { onChange, value } }) => (
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                value={value ?? ''}
                                                onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
                                                className={`mt-1 block w-full rounded-md border ${errors.nroCuota ? 'border-red-500' : 'border-gray-300'} p-2`}
                                            />
                                        )}
                                    />
                                    {errors.nroCuota && <p className="text-red-600 text-sm mt-1">{(errors.nroCuota as any).message}</p>}
                                </div>
                            </>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Adjuntar comprobante</label>

                            <div className="flex gap-3 flex-wrap">
                                <button
                                    type="button"
                                    onClick={abrirCamara}
                                    className="inline-flex items-center px-4 py-2 border border-primario-celeste-800 rounded cursor-pointer bg-white text-primario-celeste-800"
                                >
                                    <FaCamera className="mr-2" /> Tomar foto
                                </button>

                                <label className="inline-flex items-center px-4 py-2 border border-primario-celeste-800 rounded cursor-pointer bg-white text-primario-celeste-800">
                                    <FaImage className="mr-2" /> Imagen
                                    <input type="file" accept="image/*" onChange={onFileChange} className="hidden" />
                                </label>

                                <label className="inline-flex items-center px-4 py-2 border border-primario-celeste-800 rounded cursor-pointer bg-white text-primario-celeste-800">
                                    <FaFilePdf className="mr-2" /> PDF
                                    <input type="file" accept="application/pdf" onChange={onFileChange} className="hidden" />
                                </label>

                                {fileName && (
                                    <div className="flex items-center gap-3 ml-2">
                                        <div className="text-sm">{fileName}</div>
                                        <button type="button" onClick={removeFile} className="text-sm text-red-600">Remover</button>
                                    </div>
                                )}
                            </div>

                            {showCamera && (
                                <div className="mt-4">
                                    <div className="w-full bg-black rounded overflow-hidden">
                                        <video
                                            key={cameraKey}
                                            ref={videoRef}
                                            muted
                                            playsInline
                                            autoPlay
                                            className="w-full max-h-96 object-contain"
                                        />
                                    </div>
                                    <div className="mt-3 flex justify-center gap-2">
                                        <button type="button" onClick={cerrarCamara} className="px-4 py-2 rounded border border-black bg-white text-black text-sm">
                                            Cancelar
                                        </button>
                                        <button type="button" onClick={sacarFoto} className="px-4 py-2 rounded bg-primario-celeste-800 text-white text-sm">
                                            Sacar foto
                                        </button>
                                    </div>
                                </div>
                            )}

                            <canvas ref={canvasRef} style={{ display: 'none' }} />
                        </div>

                        <div className="mt-6 text-center">
                            <button
                                type="submit"
                                disabled={loading}
                                className={`px-6 py-3 rounded font-semibold text-white ${loading ? 'bg-gray-400' : 'bg-primario-celeste-800'}`}
                            >
                                {loading ? 'Procesando...' : 'Informar'}
                            </button>
                        </div>
                    </form>

                    {showCamera && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                            <div className="bg-white rounded-lg overflow-hidden shadow-xl max-w-3xl w-full p-4">
                                <div className="flex justify-end">
                                    <button onClick={cerrarCamara} className="text-gray-500 hover:text-gray-700">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="mb-4">
                                    <video key={cameraKey} ref={videoRef} className="w-full rounded" />
                                </div>

                                <div className="flex justify-center space-x-4">
                                    <button
                                        onClick={sacarFoto}
                                        className="px-4 py-2 rounded bg-primario-celeste-800 text-white font-semibold flex items-center gap-2"
                                    >
                                        <FaCamera />
                                        Tomar foto
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-6 flex justify-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 rounded border border-gray-300 bg-white"
                    >
                        Volver
                    </button>
                </div>
            </div>
        </DefaultLayout>
    );
};

export default Transferencia;